import { falExclamationTriangle } from 'shared/@generated/icons/falExclamationTriangle';
import { useRef, useState, type ReactNode } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { BaseModal, type BaseModalProps, type BaseModalRef } from 'shared/components/base-modal';
import { Button } from 'shared/components/button';
import { TextInput } from 'shared/components/form/text-input';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import useApi from 'shared/library/use-api';
import { notify } from 'shared/library/notify';
import { apiRoute } from 'shared/library/api-route';
import { useUserStore } from 'shared/state/user';

export type ReportIssueSubject = 'breed' | 'conversation' | 'listing';

export type ReportIssueModalProps = {
  modelId: number;
  subject: ReportIssueSubject;
  url: string;
  iconOnly?: boolean;
  className?: string;
  testID?: string;
  trigger?: BaseModalProps['trigger'];
};

type FieldErrors = Partial<Record<'description' | 'email' | 'name', string>>;

const minimumDescriptionLength = 10;

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function ReportIssueModal({
  modelId,
  subject,
  url,
  iconOnly = false,
  className,
  testID = `report-${subject}`,
  trigger,
}: ReportIssueModalProps) {
  const { t } = useTranslation('components');
  const user = useUserStore((state) => state.user);
  const modalRef = useRef<BaseModalRef>(null);
  const { request } = useApi({ notifyOnServerErrors: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  function resetForm() {
    setName(user?.name?.trim() || user?.display_name?.trim() || '');
    setEmail(user?.email?.trim() || '');
    setDescription('');
    setErrors({});
  }

  function validate() {
    const nextErrors: FieldErrors = {};

    if (!name.trim()) {
      nextErrors.name = t('report_issue_modal.name_error');
    }

    if (!isValidEmail(email.trim())) {
      nextErrors.email = t('report_issue_modal.email_error');
    }

    if (description.trim().length < minimumDescriptionLength) {
      nextErrors.description = t('report_issue_modal.description_error', {
        count: minimumDescriptionLength,
      });
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  async function submitReport() {
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await request<{ success: boolean }>(apiRoute('issues.report'), {
        name: name.trim(),
        email: email.trim(),
        description: description.trim(),
        url,
        model_id: modelId,
        model_type: subject,
      });

      modalRef.current?.close();
      notify.success({
        title: t('report_issue_modal.success_title'),
        description: t('report_issue_modal.success_description'),
      });
    } catch {
      notify.error(
        {
          title: t('report_issue_modal.error_title'),
          description: t('report_issue_modal.error_description'),
        },
        'report-issue-modal.tsx - submitReport',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <BaseModal
      ref={modalRef}
      closeOnBackdropPress={!isSubmitting}
      title={t(`report_issue_modal.title_${subject}`)}
      description={t(`report_issue_modal.intro_${subject}`)}
      footer={
        <View className={'flex-row gap-3'}>
          <Button
            text={t('report_issue_modal.cancel')}
            color={'white'}
            onPress={() => modalRef.current?.close()}
            disabled={isSubmitting}
          />
          <View className={'flex-1'}>
            <Button
              text={t('report_issue_modal.submit')}
              center={true}
              loading={isSubmitting}
              onPress={() => void submitReport()}
              testID={`${testID}-submit`}
            />
          </View>
        </View>
      }
      trigger={(modal) => {
        const openReportModal = () => {
          resetForm();
          modal.open();
        };

        if (trigger) return trigger({ ...modal, open: openReportModal });

        return iconOnly ? (
          <TouchableOpacity
            className={`shadow-brand-brown-dark/15 items-center justify-center rounded-full bg-white p-3 shadow ${className ?? ''}`}
            accessibilityLabel={t('report_issue_modal.report')}
            accessibilityRole={'button'}
            onPress={openReportModal}
            testID={`${testID}-trigger`}
          >
            <Icon icon={falExclamationTriangle} className={'text-stone-900'} />
          </TouchableOpacity>
        ) : (
          <Button
            text={t('report_issue_modal.report')}
            icon={falExclamationTriangle}
            iconPosition={'left'}
            color={'white'}
            dense={true}
            onPress={openReportModal}
            className={className}
            testID={`${testID}-trigger`}
          />
        );
      }}
    >
      <View className={'gap-4'}>
        <ReportField label={t('report_issue_modal.name')} error={errors.name}>
          <TextInput
            value={name}
            onChangeText={setName}
            autoCapitalize={'words'}
            autoComplete={'name'}
            accessibilityLabel={t('report_issue_modal.name')}
            testID={`${testID}-name`}
          />
        </ReportField>

        <ReportField label={t('report_issue_modal.email')} error={errors.email}>
          <TextInput
            value={email}
            onChangeText={setEmail}
            autoCapitalize={'none'}
            autoCorrect={false}
            autoComplete={'email'}
            keyboardType={'email-address'}
            accessibilityLabel={t('report_issue_modal.email')}
            testID={`${testID}-email`}
          />
        </ReportField>

        <ReportField label={t('report_issue_modal.description')} error={errors.description}>
          <TextInput
            value={description}
            onChangeText={setDescription}
            multiline={true}
            placeholder={t(`report_issue_modal.placeholder_${subject}`)}
            accessibilityLabel={t('report_issue_modal.description')}
            className={'min-h-32'}
            testID={`${testID}-description`}
          />
        </ReportField>
      </View>
    </BaseModal>
  );
}

function ReportField({
  children,
  error,
  label,
}: {
  children: ReactNode;
  error?: string;
  label: string;
}) {
  return (
    <View>
      <Text className={'text-brand-brown-dark mb-1.5 font-bold'}>{label}</Text>
      {children}
      {error ? <Text className={'mt-1.5 text-sm text-red-700'}>{error}</Text> : null}
    </View>
  );
}
